import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Map as MapIcon, Building2, MapPin } from 'lucide-react';
import { request } from '../api/client';
import { Tag } from '../ui';
import type { TagTone } from '../ui';
import { useAnyPermission } from '../store/auth';

/**
 * WHERE A CUSTOMER SITS, AS COLOURED CHIPS — the one definition for the whole app.
 *
 * Every register that lists dealers or farmers used to spell the territory as flat grey text, and
 * each one spelled it differently: `MH-JALGAON · Jalgaon` on the masters, `Pune · Pune` run onto the
 * end of a dealer's name in a picker, and nothing at all on the order, invoice and return registers.
 * None of them said which part was the state and which the beat, and none of them could be scanned —
 * a column of identical grey strings is read one row at a time.
 *
 * So: the address narrows outermost-first, each level in its OWN colour and with its OWN glyph.
 * The colour alone is not enough (three shades of blue-green mean nothing without a legend), which
 * is what the glyph is for — a map for the state, a building for the district, a pin for the beat.
 * The level is still spelled out in `title`, for hover and for a screen reader.
 *
 * The tones are the ones the Territories master already uses, so a beat is the same green here as
 * it is there.
 */
export const TERRITORY_LEVEL: Record<string, { tone: TagTone; icon: JSX.Element; label: string }> = {
  state: { tone: 'blue', icon: <MapIcon size={12} />, label: 'State' },
  district: { tone: 'cyan', icon: <Building2 size={12} />, label: 'District' },
  beat: { tone: 'green', icon: <MapPin size={12} />, label: 'Beat' },
};

/**
 * JUST THE LEVEL, as a capsule — for a row whose subject IS a territory.
 *
 * `TerritoryChips` draws a customer's whole address; a coverage row grouped by territory already
 * carries the name in its own column, and what it needs beside it is one mark saying which rung of
 * the tree that name sits on. It used to be a grey `default` pill reading the raw column value
 * ("district"), which is the same shape and the same colour on every row — furniture that took
 * space away from the name it was crowding, and told a reader nothing they could scan for.
 *
 * Same palette and same glyphs as the chain, so a district is the same cyan wherever it is printed.
 */
export function TerritoryLevelTag({ level, className }: { level?: string | null; className?: string }) {
  const meta = level ? TERRITORY_LEVEL[String(level).toLowerCase()] : undefined;
  if (!meta) return null;
  return (
    <Tag tone={meta.tone} icon={meta.icon} title={`${meta.label} — click the row to drill in`} className={className}>
      {meta.label}
    </Tag>
  );
}

/**
 * The territory master, flat, resolved once per session and shared by every caller.
 *
 * One query key app-wide, so ten registers on ten routes cost one fetch. It is a few hundred rows
 * and it changes about never, hence the long `staleTime`.
 *
 * GATED, AND IT DEGRADES: `/territories` needs TERRITORY.VIEW, which a Warehouse Manager and the
 * customer roles do not hold. Rather than 403 or render an empty column for them, `pathOf` simply
 * returns nothing and the component falls back to whatever name the row itself carries — less
 * detail, never a blank where a place should be.
 */
export function useTerritoryLookup() {
  const canSee = useAnyPermission(['TERRITORY.VIEW', 'TERRITORY.ADMIN']);
  const { data } = useQuery({
    queryKey: ['territories-flat'],
    queryFn: () => request<any[]>({ url: '/territories' }),
    enabled: canSee,
    staleTime: 5 * 60_000,
  });

  const byId = useMemo(
    () => new Map<string, any>((data ?? []).map((t: any) => [String(t.id), t])),
    [data],
  );

  /**
   * State → District → Beat for a node, outermost first, as an address narrows.
   *
   * The ZONE is dropped on purpose: it is the level above the state and, on a national master, the
   * same word on every row — furniture, not information. The walk is bounded at 8 hops because the
   * master is a tree only by convention; one bad parent link must never spin a render.
   */
  const pathOf = (territoryId?: string | number | null): { level: string; name: string }[] => {
    const out: { level: string; name: string }[] = [];
    let node = territoryId ? byId.get(String(territoryId)) : null;
    for (let i = 0; node && i < 8; i++) {
      if (TERRITORY_LEVEL[node.level]) out.unshift({ level: node.level, name: node.name });
      node = node.parentId ? byId.get(String(node.parentId)) : null;
    }
    return out;
  };

  return { byId, pathOf, canSee, loaded: !!data };
}

/**
 * A LIST of territory nodes — a STAFF member's book, which is not the same shape as a customer's
 * address.
 *
 * A dealer hangs off ONE node, so their place is a path and every chip is an ancestor of the next.
 * A rep is assigned to SEVERAL, at levels that need not relate: "Katol" and "Maharashtra" side by
 * side means one beat and one whole state, not a beat inside that state. Same palette, because a
 * beat is a beat wherever it is printed — but no path is implied and none is drawn.
 *
 * `max` caps the row, and what is cut is NAMED rather than silently dropped: `+3` carries the three
 * names in its tooltip. The previous caption kept two and said nothing about the rest, so a rep on
 * five beats looked like a rep on two.
 */
export function TerritoryNodeChips({
  nodes, max = 3, emptyText = 'no territory assigned', hideWhenEmpty, className,
}: {
  nodes: { id?: string; level: string; name: string }[];
  max?: number;
  emptyText?: string;
  hideWhenEmpty?: boolean;
  className?: string;
}) {
  if (!nodes.length) {
    if (hideWhenEmpty) return null;
    // A rep with no territory is in nobody's patch — no visit plan reaches them and no geographic
    // filter on this page will ever show them. That is a gap, so it reads as one.
    return <span className="type-caption whitespace-nowrap text-warning-600 dark:text-warning-500">{emptyText}</span>;
  }
  const shown = nodes.slice(0, max);
  const rest = nodes.slice(max);
  return (
    <div className={`flex flex-nowrap items-center gap-1 whitespace-nowrap ${className ?? ''}`}>
      {shown.map((n, i) => {
        const meta = TERRITORY_LEVEL[n.level];
        return (
          <Tag key={n.id ?? `${n.level}-${n.name}-${i}`} tone={meta?.tone ?? 'neutral'} icon={meta?.icon} title={meta?.label ?? n.level}>
            {n.name}
          </Tag>
        );
      })}
      {rest.length > 0 && (
        <Tag tone="neutral" title={rest.map((n) => n.name).join(', ')}>+{rest.length}</Tag>
      )}
    </div>
  );
}

export interface TerritoryChipsProps {
  /** The customer's territory node. Renders the whole State → District → Beat chain. */
  territoryId?: string | number | null;
  /**
   * The leaf's name as the ROW already knows it (many list endpoints send `territoryName`). Used
   * when the master cannot be read or does not hold the id — a name is worth more than a dash.
   */
  fallbackName?: string | null;
  /**
   * The whole State → District → Beat path as the ROW already carries it (the farmer list sends
   * `territoryPath`). Used when the master cannot be read — a Sales Person does not hold
   * TERRITORY.VIEW — so they still see the full chain rather than a bare beat name.
   */
  fallbackPath?: { level: string; name: string }[] | null;
  /** An extra chip for the town, dropped when a level of the chain already says the same word. */
  city?: string | null;
  /** What to show when there is no place on file at all. Amber, because it is a gap, not a value. */
  emptyText?: string;
  /** Render nothing at all when there is no place on file (for a cell that is already dense). */
  hideWhenEmpty?: boolean;
  /**
   * Let the chain wrap onto a second line. OFF by default, and deliberately so.
   *
   * A wrapped chain makes the whole table row taller — every row, including the ones whose chain
   * happens to fit — and a register is scanned down its rows, so buying one more line of address
   * costs a third of the rows on screen. These tables already scroll sideways, which is the cheap
   * direction: overflow there costs nothing that is not already being paid.
   *
   * Turn it on where the container is NOT a table row and vertical space is free.
   */
  wrap?: boolean;
  className?: string;
}

/**
 * The chips. Give it a `territoryId` and it draws the chain; give it only a `fallbackName` and it
 * draws that one chip. It never draws an em-dash: a customer with nowhere on file cannot be routed
 * to anybody, which is a fact worth reading, not a blank to skip over.
 */
export function TerritoryChips({
  territoryId, fallbackName, fallbackPath, city, emptyText = 'no territory on file', hideWhenEmpty, wrap, className,
}: TerritoryChipsProps) {
  const { pathOf } = useTerritoryLookup();
  const looked = pathOf(territoryId);
  const path = looked.length ? looked : (fallbackPath ?? []).filter((p) => TERRITORY_LEVEL[p.level]);
  // The town is dropped when a level already names it — "Pune · [Pune]" is the same word twice.
  const extraCity = city && !path.some((p) => p.name.toLowerCase() === String(city).toLowerCase())
    ? city
    : null;
  const leaf = !path.length && fallbackName ? String(fallbackName) : null;

  if (!path.length && !leaf && !extraCity) {
    if (hideWhenEmpty) return null;
    return <span className="type-caption whitespace-nowrap text-warning-600 dark:text-warning-500">{emptyText}</span>;
  }

  return (
    <div className={`flex items-center gap-1 ${wrap ? 'flex-wrap' : 'flex-nowrap whitespace-nowrap'} ${className ?? ''}`}>
      {path.map((p) => (
        <Tag key={p.level} tone={TERRITORY_LEVEL[p.level].tone} icon={TERRITORY_LEVEL[p.level].icon} title={TERRITORY_LEVEL[p.level].label}>
          {p.name}
        </Tag>
      ))}
      {/* The master was not readable (or does not hold this node) — show what the row knows, in the
          beat's own colour, because a leaf territory is what these names almost always are. */}
      {leaf && <Tag tone="green" icon={<MapPin size={12} />} title="Territory">{leaf}</Tag>}
      {extraCity && <Tag tone="neutral" icon={<MapPin size={12} />} title="City">{extraCity}</Tag>}
    </div>
  );
}
